import { ClarifyingQuestionsResponse } from "../../models";

export const OTHER_OPTION = "__other__";

export const MAX_PREDEFINED_OPTIONS = 3;

export const CLARIFY_STORAGE_KEY = "deepchat-clarify-inprogress";

// Backend groups clarifications into two kinds, distinguished by the `phase` field on the
// payload: a direct "Clarifying Question" the user answers, and a set of "Enhanced Questions"
// — more-specific rephrasings the user can pick from.
export const ENHANCED_QUESTIONS_PHASE = 2;

export interface ClarifyingAnswer {
  field_hint: string;
  question: string;
  // Exact-match selections from the predefined options. These are safe to apply as structured search filters
  values: string[];
  // Free-text the user typed via the "Other" field. This is NOT applied as a filter, it is sent to the LLM as plain-text context instead.
  freeText?: string;
}

export interface ClarifyingQuestionsProps {
  clarification: ClarifyingQuestionsResponse;
  answered: boolean;
  // Scopes the in-progress cache so another thread's saved answers are never restored.
  sessionId?: string;
  // The user's previously chosen values, reconstructed from thread history when a persisted
  // thread is reopened (there is no localStorage draft in that case). Used to render the
  // answered module with the original selection checked. Values not matching a predefined
  // option are treated as free-text ("Other").
  prefillValues?: string[];
  onSubmit: (answers: ClarifyingAnswer[]) => void;
  onSkip: () => void;
  onSkipAndTurnOff?: () => void;
  // Enhanced-questions flow: the user picked (or completed) a more specific rephrasing, which is
  // sent immediately as the new query.
  onSelectEnhancedPrompt?: (prompt: string) => void;
}

export interface QuestionSelection {
  selected: Set<string>;
  otherText: string;
}

export interface PersistedClarifyState {
  sessionId?: string;
  questionIds: string[];
  selections: Record<string, { selected: string[]; otherText: string }>;
  skippedIds: string[];
}

export function initialSelection(): QuestionSelection {
  return { selected: new Set<string>(), otherText: "" };
}

// Humanizes a field_hint for use in placeholders/labels ("product_molecule" -> "product molecule").
export function readableHint(fieldHint: string): string {
  return (fieldHint || "option").replace(/_/g, " ").replace(/name$/i, "").trim();
}

// Returns null when the saved entry is absent, malformed, or belongs to a different
// session or question set — so a new thread never inherits stale answers.
export function readPersistedState(sessionId: string | undefined, questionIds: string[]): PersistedClarifyState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CLARIFY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedClarifyState;
    if ((parsed?.sessionId ?? undefined) !== (sessionId ?? undefined)) return null;
    const savedIds = parsed?.questionIds ?? [];
    const same =
      Array.isArray(savedIds) &&
      savedIds.length === questionIds.length &&
      savedIds.every((id, i) => id === questionIds[i]);
    return same ? parsed : null;
  } catch {
    return null;
  }
}

export function clearPersistedState() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CLARIFY_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
